import { useCallback, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import type { MarkdownImportCompletedData, MarkdownImportStartedData } from '../../types.ts';

const MEBIBYTE = 1024 * 1024;

export type MarkdownTotalSizeBucket = '0-1MiB' | '1-5MiB' | '5-10MiB' | '10-25MiB' | '25-50MiB';

export const getMarkdownTotalSizeBucket = (totalSizeBytes: number): MarkdownTotalSizeBucket => {
	if (totalSizeBytes <= MEBIBYTE) return '0-1MiB';
	if (totalSizeBytes <= 5 * MEBIBYTE) return '1-5MiB';
	if (totalSizeBytes <= 10 * MEBIBYTE) return '5-10MiB';
	if (totalSizeBytes <= 25 * MEBIBYTE) return '10-25MiB';
	return '25-50MiB';
};

type AttemptState = {
	startedAt: number;
	completed: boolean;
};

type MarkdownImportAnalytics = {
	startImport: (data: MarkdownImportStartedData) => void;
	completeImport: (data: MarkdownImportCompletedData) => void;
};

export const useMarkdownImportAnalytics = (
	now: () => number = Date.now,
): MarkdownImportAnalytics => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const attemptRef = useRef<AttemptState | null>(null);

	const startImport = useCallback(
		(data: MarkdownImportStartedData) => {
			attemptRef.current = { startedAt: now(), completed: false };
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'started',
					actionSubject: 'markdownImport',
					source: 'editorRightRail',
					attributes: {
						mode: data.mode,
						selectedFileCount: data.selectedFileCount,
						totalSizeBucket: getMarkdownTotalSizeBucket(data.totalSizeBytes),
					},
				},
			}).fire();
		},
		[createAnalyticsEvent, now],
	);

	const completeImport = useCallback(
		(data: MarkdownImportCompletedData) => {
			const attempt = data.result === 'validation_failure' ? null : attemptRef.current;
			if (data.result !== 'validation_failure' && attempt?.completed) return;
			if (attempt) attempt.completed = true;
			if (data.result === 'validation_failure') attemptRef.current = null;

			const warningCodes = Array.from(new Set(data.warnings?.map(({ code }) => code) ?? []));
			const warningCount = data.warnings?.reduce((total, warning) => total + warning.count, 0) ?? 0;
			const durationMs = attempt ? Math.max(0, Math.round(now() - attempt.startedAt)) : undefined;

			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'completed',
					actionSubject: 'markdownImport',
					source: 'editorRightRail',
					attributes: {
						mode: data.mode,
						result: data.result,
						selectedFileCount: data.selectedFileCount,
						successfulFileCount: data.successfulFileCount,
						failedFileCount: data.failedFileCount,
						warningCount,
						warningCodes,
						...(data.errorCode ? { errorCode: data.errorCode } : {}),
						...(durationMs === undefined ? {} : { durationMs }),
					},
				},
			}).fire();
		},
		[createAnalyticsEvent, now],
	);

	return { startImport, completeImport };
};
